/* THE PROBLEM LIST, after Weed: a numbered list, each problem with its own SOAP thread (subjective, objective, assessment,
   plan), never a feed. The numbers are fixed (1 to 5, the same every time, whatever order things happen in) and a problem
   that is not active says so; nothing is appended.

   The world holds no problem list, so none is invented. A problem here is a body system the nurse charts a number for
   (circulation, oxygenation, ventilation, temperature, consciousness), and everything under it is what the agent was
   returned:
   S  the pages and notes whose words name the problem, verbatim (the words are listed beside the problem below);
   O  the latest charted number and how old it is, and the observations before it;
   A  the pattern (OK, degrading, failing) and the limit it crosses, from the limits in force;
   P  the orders on the chart aimed at it, with their status, and the aim the order table gives (intent.ts). */
import { duration } from '../../lib/format.ts'
import { humanise } from '../../lib/schema.ts'
import type { Chart, ChartRow } from '../../lib/types.ts'
import { crossedLimit, type AlarmLimits, type Alarmed } from './alarms.ts'
import { DEPRESSORS, FEVER, OXYGEN, PRESSORS, VOLUME } from './intent.ts'
import { MENTAL, PATTERN_TEXT, SEDATIVES, UNIT, patternOf, seriesOf, worst, type Param, type Pattern } from './theatre.ts'

export type ProblemKey = 'circulation' | 'oxygenation' | 'ventilation' | 'temperature' | 'consciousness'
type Def = { key: ProblemKey; title: string; params: Param[]; words: RegExp; orders: readonly string[] }

/** Fixed order, fixed numbers. `words` is what makes a page or note belong to a problem; `orders` the chart orders aimed at it. */
export const PROBLEMS: readonly Def[] = [
  { key: 'circulation', title: 'Circulation', params: ['map', 'hr'], words: /\b(HR|MAP|heart|pulse|pressure|BP|perfusion|shock)\b/i, orders: [...VOLUME, ...PRESSORS, ...DEPRESSORS] },
  { key: 'oxygenation', title: 'Oxygenation', params: ['spo2'], words: /\b(SpO2|oxygen|saturation|sats?|hypox\w*)\b/i, orders: OXYGEN },
  { key: 'ventilation', title: 'Ventilation', params: ['rr'], words: /\b(RR|breath\w*|respirat\w*|ventilat\w*|airway)\b/i, orders: ['ventilator', 'intubate', 'niv', 'hfnc'] },
  { key: 'temperature', title: 'Temperature', params: ['temp'], words: /\b(temp\w*|fever|febrile|pyrexi\w*)\b/i, orders: FEVER },
  { key: 'consciousness', title: 'Consciousness', params: ['mental'], words: /\b(confus\w*|drows\w*|alert|conscious\w*|sedat\w*|neuro\w*)\b/i, orders: SEDATIVES },
]

export type Problem = {
  n: number
  key: ProblemKey
  title: string
  active: boolean
  pattern: Pattern
  s: { t: number; text: string }[]
  o: string[]
  a: string
  p: { id: string; label: string; status: string }[]
}

const NAME: Record<Param, string> = { hr: 'HR', map: 'MAP', spo2: 'SpO2', rr: 'RR', temp: 'Temp', mental: 'Consciousness' }
const OF_ALARMED = (p: Param): p is Alarmed => p === 'hr' || p === 'map' || p === 'spo2' || p === 'rr'

function seen(rows: readonly ChartRow[], p: Param, now: number): string {
  const r = rows[rows.length - 1]
  const m = r[p]
  const v = p === 'mental' ? MENTAL[Math.max(0, Math.min(3, Math.round(m.value)))] : `${p === 'temp' ? m.value.toFixed(1) : Math.round(m.value)} ${UNIT[p]}`
  const age = now - (m.at ?? r.t)
  return `${NAME[p]} ${v}, charted ${age < 1 ? 'now' : `${duration(age)} ago`}`
}

/** The five problems for the chart as last read. Before the vitals are read every problem is empty and says so. */
export function problemsOf(chart: Pick<Chart, 't' | 'vitals' | 'orders' | 'pages' | 'notes'>, l: AlarmLimits): Problem[] {
  const rows = chart.vitals
  const entries = [...chart.pages, ...chart.notes].sort((a, b) => a.t - b.t)
  return PROBLEMS.map((d, i) => {
    const pattern = worst(d.params.map((p) => patternOf(p, seriesOf(rows, p), l)))
    const o = rows.length ? d.params.map((p) => seen(rows, p, chart.t)) : []
    const crossed = rows.length
      ? d.params.flatMap((p) => {
          if (!OF_ALARMED(p)) return []
          const text = crossedLimit(p, rows[rows.length - 1][p].value, l)
          return text ? [text] : []
        })
      : []
    const a = !rows.length ? 'Vitals not read.' : crossed.length ? `${PATTERN_TEXT[pattern]}: ${crossed.join('; ')}` : pattern === 'ok' ? 'OK: inside its limits.' : `${PATTERN_TEXT[pattern]}.`
    return {
      n: i + 1,
      key: d.key,
      title: d.title,
      active: rows.length > 0 && pattern !== 'ok',
      pattern,
      s: entries.filter((e) => d.words.test(e.text)).map((e) => ({ t: e.t, text: e.text })),
      o,
      a,
      p: chart.orders.filter((x) => d.orders.includes(x.order)).map((x) => ({ id: x.id, label: humanise(x.order), status: x.status.replace(/_/g, ' ') })),
    }
  })
}
